
/**
 * Platform Integration Service
 * Handles connection state and interactions for advertising and social platforms.
 * Persists connection status via Supabase.
 */

import { supabase } from '../supabase';

export const googleSuiteServices = [
    'google_analytics', 'google_ads', 'google_lsa',
    'google_business', 'youtube', 'google_drive',
    'google_search_console', 'google_tag_manager',
    'google_adsense', 'google_photos', 'gmail', 'google', 'google_calendar'
];

class PlatformIntegrationService {
  constructor() {
    // Map platform IDs to internal names/keys if needed
    this.platforms = {
      // Advertising
      facebook: { name: 'Meta Ads', domain: 'facebook.com' },
      google: { name: 'Google Ads', domain: 'google.com' },
      linkedin: { name: 'LinkedIn Ads', domain: 'linkedin.com' },
      tiktok: { name: 'TikTok Ads', domain: 'tiktok.com' },
      twitter: { name: 'Twitter Ads', domain: 'x.com' },
      ctv: { name: 'Roku Advertising', domain: 'roku.com' },
      google_lsa: { name: 'Google LSA', domain: 'google.com' },
      amazon_ads: { name: 'Amazon Ads', domain: 'amazon.com' },
      google_ads: { name: 'Google Ads', domain: 'google.com' }, // Added for Suite

      // Data Enrichment
      happierleads: { name: 'Happierleads', domain: 'happierleads.com' },
      apollo: { name: 'Apollo.io', domain: 'apollo.io' },
      lusha: { name: 'Lusha', domain: 'lusha.com' },
      hunter: { name: 'Hunter', domain: 'hunter.io' },
      clearbit: { name: 'Clearbit', domain: 'clearbit.com' },
      zoominfo: { name: 'ZoomInfo', domain: 'zoominfo.com' },
      cognism: { name: 'Cognism', domain: 'cognism.com' },
      rocketreach: { name: 'RocketReach', domain: 'rocketreach.co' },

      // Social Media
      facebook_page: { name: 'Facebook Page', domain: 'facebook.com' },
      instagram: { name: 'Instagram Business', domain: 'instagram.com' },
      twitter_social: { name: 'X (Twitter)', domain: 'x.com' },
      linkedin_page: { name: 'LinkedIn Page', domain: 'linkedin.com' },
      tiktok_social: { name: 'TikTok Profile', domain: 'tiktok.com' },
      pinterest: { name: 'Pinterest', domain: 'pinterest.com' },
      google_business: { name: 'Google Business Profile', domain: 'google.com' },
      youtube: { name: 'YouTube', domain: 'youtube.com' },
      reddit: { name: 'Reddit', domain: 'reddit.com' },
      snapchat: { name: 'Snapchat', domain: 'snapchat.com' },
      linkedin_personal: { name: 'LinkedIn Personal', domain: 'linkedin.com' },

      // Storage & Productivity
      google_drive: { name: 'Google Drive', domain: 'google.com' },
      google_photos: { name: 'Google Photos', domain: 'google.com' }, // Added for Suite
      canva: { name: 'Canva', domain: 'canva.com' },
      dropbox: { name: 'Dropbox', domain: 'dropbox.com' },
      onedrive: { name: 'OneDrive', domain: 'microsoft.com' },
      box: { name: 'Box', domain: 'box.com' },

      // Email Marketing
      mailchimp: { name: 'Mailchimp', domain: 'mailchimp.com' },
      sendgrid: { name: 'SendGrid', domain: 'sendgrid.com' },
      hubspot: { name: 'HubSpot', domain: 'hubspot.com' },
      salesforce: { name: 'Salesforce', domain: 'salesforce.com' },
      housecallpro: { name: 'Housecall Pro', domain: 'housecallpro.com' },
      gmail: { name: 'Gmail', domain: 'google.com' },
      resend: { name: 'Resend', domain: 'resend.com' },

      // Website & Commerce
      wordpress: { name: 'WordPress', domain: 'wordpress.org' },

      // Developer & API
      openrouter: { name: 'OpenRouter', domain: 'openrouter.ai' },
      wix: { name: 'Wix', domain: 'wix.com' },
      google_analytics: { name: 'Google Analytics 4', domain: 'google.com' },
      google_search_console: { name: 'Google Search Console', domain: 'google.com' }, // Added for Suite
      google_tag_manager: { name: 'Google Tag Manager', domain: 'google.com' }, // Added for Suite
      google_adsense: { name: 'Google AdSense', domain: 'google.com' } // Added for Suite
    };
  }

  /**
   * Get all connections for a workspace
   * @param {string} workspaceId
   */
  async getConnections(workspaceId) {
    try {
      const { data, error } = await supabase
        .from('platform_connections')
        .select('id, workspace_id, platform_id, status, last_sync, accounts, created_at, updated_at')
        .eq('workspace_id', workspaceId);

      if (error) {
        console.warn('Error fetching connections (table might be missing in dev):', error.message);
        return [];
      }
      return data || [];
    } catch (error) {
      console.error('Error fetching connections:', error);
      return [];
    }
  }

  /**
   * Get credentials for a specific platform connection
   * @param {string} workspaceId
   * @param {string} platformId
   */
  async getCredentials(workspaceId, platformId) {
    try {
      // 1. Try fetching specific credentials first
      const { data, error } = await supabase
        .from('platform_connections')
        .select('credentials')
        .eq('workspace_id', workspaceId)
        .eq('platform_id', platformId)
        .maybeSingle();

      if (data?.credentials) return data.credentials;

      if (error && error.code !== 'PGRST116') throw error; // Ignore not found error if we are falling back
      return null;
    } catch (error) {
      console.error('Error fetching credentials:', error);
      return null;
    }
  }

  /**
   * Connect a platform (save credentials)
   * @param {string} workspaceId
   * @param {string} platformId
   * @param {object} credentials - Object containing apiKey, accessToken, adAccountId, etc.
   */
  async connect(workspaceId, platformId, credentials) {
    // Fallback for dynamically added admin integrations
    if (!this.platforms[platformId]) {
        this.platforms[platformId] = { name: platformId.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()), domain: 'unknown' };
    }

    try {
        const payload = {
            workspace_id: workspaceId,
            platform_id: platformId,
            credentials: credentials, // Store the whole object
            status: 'active',
            last_sync: new Date().toISOString(),
            accounts: 1 // Default to 1, in real app would be fetched from API
        };

        const { error } = await supabase
            .from('platform_connections')
            .upsert(payload, { onConflict: 'workspace_id, platform_id' });

        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error connecting platform:', error);
        throw error;
    }
  }

  /**
   * Disconnect a platform
   * @param {string} workspaceId
   * @param {string} platformId
   */
  async disconnect(workspaceId, platformId) {
    try {
        const { error } = await supabase
            .from('platform_connections')
            .delete()
            .eq('workspace_id', workspaceId)
            .eq('platform_id', platformId);

        if (error) throw error;
        return true;
    } catch (error) {
        console.error('Error disconnecting platform:', error);
        throw error;
    }
  }

  /**
   * Fetch ad accounts or pixels from the platform
   * @param {string} workspaceId
   * @param {string} platformId
   */
  async getPixels(workspaceId, platformId) {
    try {
      const credentials = await this.getCredentials(workspaceId, platformId);
      if (!credentials || !credentials.accessToken) {
        return [];
      }

      const accessToken = credentials.accessToken;
      const results = [];

      // Demo Mode Mock Data
      if (accessToken === 'DEMO_MODE_KEY') {
        if (platformId === 'facebook_page' || platformId === 'facebook') {
          return [
            { id: 'mock_fb_page_1', name: 'Mock FB Page (Demo)', status: 'active', category: 'Local Business' },
            { id: 'mock_fb_page_2', name: 'Mock FB Shop (Demo)', status: 'active', category: 'Shopping' }
          ];
        }
        if (platformId === 'instagram') {
          return [
            { id: 'mock_ig_account_1', name: 'Mock IG Account (Demo)', status: 'active', category: 'Creator' }
          ];
        }
        if (['linkedin_page', 'linkedin_ads'].includes(platformId)) {
          return [
            { id: 'mock_li_org_1', name: 'Mock LI Organization (Demo)', status: 'active', category: 'Company' }
          ];
        }
        if (platformId === 'google_ads' || platformId === 'google') {
          return [
             { id: 'mock_gads_1', name: 'Mock Google Ad Account', status: 'active', category: 'Ads' }
          ];
        }
        return [
           { id: `mock_${platformId}_1`, name: `Mock ${platformId} Pixel`, status: 'active', category: 'Demo' }
        ];
      }

      if (['tiktok', 'tiktok_social'].includes(platformId)) {
          const listRes = await fetch('https://open.tiktokapis.com/v2/user/info/?fields=open_id,display_name', {
              headers: { Authorization: `Bearer ${accessToken}` }
          });
          if (listRes.ok) {
              const data = await listRes.json();
              if (data.data?.user) {
                  results.push({
                      id: data.data.user.open_id,
                      name: data.data.user.display_name,
                      status: 'active',
                      category: 'TikTok Profile'
                  });
              }
          }
      } else if (platformId === 'facebook_page' || platformId === 'instagram' || platformId === 'facebook') {
        const response = await fetch(`https://graph.facebook.com/v19.0/me/accounts?access_token=${accessToken}&fields=access_token,name,id,instagram_business_account,tasks&limit=100`);
        const data = await response.json();

        if (data.error) throw new Error(data.error.message);

        if (data.data) {
          if (platformId === 'instagram') {
             // For Instagram, only list pages with an associated instagram_business_account
             results.push(...data.data.filter(page => page.instagram_business_account).map(page => ({
                 id: page.instagram_business_account.id,
                 name: `IG: ${page.name}`, // using page name as fallback since ig account name isn't requested in fields
                 status: 'active',
                 category: 'Instagram Business Account'
             })));
          } else {
             results.push(...data.data.map(page => ({
               id: page.id,
               name: page.name,
               status: 'active',
               category: page.category
             })));
          }
        }
      } else if (['linkedin_page', 'linkedin_ads'].includes(platformId)) {
        const listRes = await fetch('https://api.linkedin.com/v2/organizationAcls?q=roleAssignee&role=ADMINISTRATOR&state=APPROVED', {
            headers: { Authorization: `Bearer ${accessToken}` }
        });
        if (listRes.ok) {
            const data = await listRes.json();
            for (const org of (data.elements || [])) {
                // org.organization usually looks like 'urn:li:organization:12345'
                const orgId = org.organization.split(':').pop();

                results.push({
                    id: orgId,
                    name: `Organization ID: ${orgId}`,
                    status: 'active',
                    category: 'Organization'
                });
            }
        }
      } else if (platformId === 'google_business') {
          const accountsUrl = 'https://mybusinessaccountmanagement.googleapis.com/v1/accounts';
          let accountsRes = await fetch(accountsUrl, { headers: { Authorization: `Bearer ${accessToken}` } });
          if (accountsRes.ok) {
              const accountsData = await accountsRes.json();
              for (const account of (accountsData.accounts || [])) {
                  const locationsUrl = `https://mybusinessbusinessinformation.googleapis.com/v1/${account.name}/locations?readMask=name,title`;
                  const locationsRes = await fetch(locationsUrl, { headers: { Authorization: `Bearer ${accessToken}` } });
                  if (locationsRes.ok) {
                      const locationsData = await locationsRes.json();
                      for (const location of (locationsData.locations || [])) {
                          results.push({
                              id: `${account.name}|${location.name}`, // Store both for Edge Functions
                              name: `${account.accountName || 'Account'} - ${location.title}`,
                              status: 'active',
                              category: 'Location'
                          });
                      }
                  }
              }
          }
      } else if (platformId === 'google_ads' || platformId === 'google') {
          // Google Ads frontend discovery requires a Developer Token, which is a backend secret.
          // Due to the complexity of Google Ads Developer Token requirements on the frontend,
          // we encourage users to use the manual ID input box for Google Ads if they cannot see a list.
          // In a production environment, this fetch logic would be proxied through an Edge Function.
      } else if (platformId === 'google_analytics') {
          const listRes = await fetch('https://analyticsadmin.googleapis.com/v1beta/accountSummaries', {
              headers: { Authorization: `Bearer ${accessToken}` }
          });
          if (listRes.ok) {
              const data = await listRes.json();
              for (const account of (data.accountSummaries || [])) {
                  for (const prop of (account.propertySummaries || [])) {
                      if (prop.property) {
                          results.push({
                              id: prop.property.split('/')[1],
                              name: prop.displayName,
                              status: 'active',
                              category: 'Property'
                          });
                      }
                  }
              }
          }
      } else if (platformId === 'youtube') {
          const channelDataRes = await fetch('https://www.googleapis.com/youtube/v3/channels?part=snippet,contentDetails&mine=true', {
              headers: { Authorization: `Bearer ${accessToken}` }
          });
          if (channelDataRes.ok) {
              const channelData = await channelDataRes.json();
              for (const channel of (channelData.items || [])) {
                  results.push({
                      id: channel.id,
                      name: channel.snippet?.title || 'YouTube Channel',
                      status: 'active',
                      category: 'Channel'
                  });
              }
          }
      } else if (platformId === 'google_search_console') {
          const listUrl = 'https://searchconsole.googleapis.com/webmasters/v3/sites';
          const listRes = await fetch(listUrl, { headers: { Authorization: `Bearer ${accessToken}` } });
          if (listRes.ok) {
              const data = await listRes.json();
              for (const site of (data.siteEntry || [])) {
                  results.push({
                      id: site.siteUrl,
                      name: site.siteUrl,
                      status: 'active',
                      category: 'Site'
                  });
              }
          }
      }

      return results;

    } catch (error) {
      console.error('Error fetching pixels/pages:', error);
      throw error;
    }
  }
}

export const platformService = new PlatformIntegrationService();
